import { IntakeFeedRow } from "@/models/intakeFeedRow";
import { normalizePARow } from "@/composables/usePATable";
import { caseModule, withMedicalNecessity } from "@/models/caseModules";
export const STATE_LABELS = {
    NOT_STARTED: "Not started",
    READY: "Ready",
    IN_PROGRESS: "In progress",
    WAITING: "Waiting",
    NEEDS_ATTENTION: "Needs attention",
    DONE: "Done",
    NOT_NEEDED: "Not needed",
    CANCELLED: "Cancelled",
};
export const OPEN_STATES = new Set([
    "NOT_STARTED",
    "READY",
    "IN_PROGRESS",
    "WAITING",
    "NEEDS_ATTENTION",
]);
export const STATE_TONES = {
    NEEDS_ATTENTION: "attention",
    READY: "ready",
    IN_PROGRESS: "progress",
    WAITING: "waiting",
    NOT_STARTED: "idle",
    DONE: "done",
    NOT_NEEDED: "muted",
    CANCELLED: "muted",
};
export class WorklistRow {
    id;
    kind;
    pa;
    order;
    document;
    chips;
    isOpen;
    workPriority;
    priority;
    soonestExpiry;
    dueAt;
    lastActivityAt;
    createdOn;
    hasPatient;
    raw;
    casePa;
    /** An intake perspective acts on the intake record, as the Intake tab does, even once the case has a PA. */
    constructor(raw, perspective = "case") {
        this.raw = raw;
        this.id = raw.id;
        this.casePa = raw.pa ? normalizePARow(raw.pa) : null;
        this.order = raw.order ? new IntakeFeedRow(raw.order, "order") : null;
        this.document = raw.document ? new IntakeFeedRow(raw.document, "document") : null;
        this.pa = perspective === "intake" && this.intake ? null : this.casePa;
        this.kind = this.pa ? "pa" : this.order ? "order" : "document";
        this.isOpen = !!raw.isOpen;
        this.workPriority = raw.workPriority;
        this.priority = raw.priority;
        this.soonestExpiry = raw.soonestExpiry;
        this.dueAt = raw.dueAt;
        this.lastActivityAt = raw.lastActivityAt;
        this.createdOn = raw.createdOn;
        this.hasPatient = raw.patientId !== null;
        this.chips = withMedicalNecessity(raw.activities).map((activity) => this.chip(activity));
    }
    /** The record the row acts on: the PA once there is one, else the intake order, else the document. */
    get primary() {
        return this.pa ?? this.order ?? this.document;
    }
    get intake() {
        return this.order ?? this.document;
    }
    get typeLabel() {
        return this.pa ? "Prior auth" : "Intake";
    }
    get patientName() {
        return (this.casePa ?? this.intake)?.patientName ?? "";
    }
    get mrn() {
        return this.casePa?.mrn || this.intake?.mrn || "";
    }
    get medication() {
        return this.casePa?.medication || this.intake?.medication || "";
    }
    get payer() {
        return this.casePa?.insurance || this.intake?.insurance || "";
    }
    /** The record whose provider the row shows; name and phone both come from it, so they describe one person. */
    get providerSource() {
        if (this.casePa?.provider)
            return this.casePa;
        if (this.intake?.provider)
            return this.intake;
        return this.casePa ?? this.intake;
    }
    get provider() {
        return this.providerSource?.provider || "";
    }
    get providerPhone() {
        return this.providerSource?.providerPhone || "";
    }
    get providerPhoneExtension() {
        const source = this.providerSource;
        return source instanceof IntakeFeedRow ? source.providerPhoneExtension : "";
    }
    get insurance() {
        return this.payer;
    }
    get planType() {
        return this.casePa?.planType ?? "";
    }
    get priorAuthNumber() {
        return this.casePa?.priorAuthNumber ?? "";
    }
    get prescriptionNumber() {
        return this.casePa?.prescriptionNumber ?? "";
    }
    get dateCreated() {
        return this.casePa?.dateCreated ?? this.intake?.dateReceived ?? this.createdOn;
    }
    get dateReceived() {
        return this.intake?.dateReceived ?? this.casePa?.dateCreated ?? this.createdOn;
    }
    get dateModified() {
        return this.primary?.dateModified ?? null;
    }
    get modifiedBy() {
        return this.primary?.modifiedBy ?? "";
    }
    get documentTypes() {
        return this.document?.documentTypes ?? "";
    }
    get sentBy() {
        return this.document?.sentBy ?? "";
    }
    get isSnoozed() {
        return !!this.raw.isSnoozed;
    }
    /** Where opening the row goes: the open step if one is open, else the primary record. */
    get navigateTo() {
        return this.chips.find((chip) => chip.isOpen && chip.to)?.to ?? this.primary?.navigateTo ?? null;
    }
    chip(activity) {
        const isOpen = OPEN_STATES.has(activity.state);
        return {
            id: activity.id,
            moduleKey: activity.moduleKey,
            moduleLabel: caseModule(activity.moduleKey).label,
            state: activity.state,
            stateLabel: STATE_LABELS[activity.state],
            tone: STATE_TONES[activity.state],
            text: activity.outcome || activity.reason || activity.label || STATE_LABELS[activity.state],
            isOpen,
            to: this.routeFor(activity),
        };
    }
    routeFor(activity) {
        if (activity.orderInfo && this.casePa)
            return this.casePa.navigateTo;
        if (activity.intakeOrder && this.order)
            return this.order.navigateTo;
        if (activity.intakeDocument && this.document)
            return this.document.navigateTo;
        return this.order?.navigateTo ?? null;
    }
}
